import React, { useState, useImperativeHandle, forwardRef } from "react";
import { Typography, Box, FormControlLabel, Checkbox } from '@mui/material';
import * as taskTemplateStyle from 'mui-components/kart/fieldmap/tasktemplates/TaskTemplateStyle';
import MuiTextField from 'components/mui3-forms/MuiTextField';
import MuiSelectMobileDropdown from 'components/mui3-forms/MuiSelectMobileDropdown';
import MuiCollapseComponent from 'components/mui3-forms/MuiCollapseComponent';
import { pesticideUnitOptions } from "utils/DynamicFields";
import MuiMultiSelectBottomSheet from '../MuiMultiSelectBottomSheet';
import MuiTextFieldArea from 'components/mui3-forms/MuiTextFieldArea';
import BottomSheetSubNestedDropdown from "../BottomSheetSubNestedDropdown";
import BottomSheetNestedDropdown from "../BottomSheetNestedDropdown";
import AddVehicleModal from 'mui-mobile-components/kart/fieldmap/vehicleModals/AddVehicleModal';
import _ from 'lodash';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TimerModal from 'mui-components/kart/fieldmap/tasktemplates/TimerModal';

const AddTaskWaterJournalTemplate2 = forwardRef((props, ref) => {
  const { intl, taskDetails, createdVehicleId, fieldMasterData, activitiesCommon, onFieldChange, onCloseVehicleDialog, formData, handleNumericValidation, vehicleOptions, formErrors, waterJournal, openTimerDialog, handleCancelTimerButton, handleOkTimerButton, handleHoursChange, handleMinutesChange, currentTimerHourValue, currentTimerMinutesValue, isTimerOpen } = props;
  let pesticideDropdowns = _.filter(activitiesCommon, ['activityId', 'act19'])[0].dropdowns[2].elements;
  let growthStageDropdowns = _.filter(activitiesCommon, ['activityId', 'act19'])[0].dropdowns[3].elements;
  // eslint-disable-next-line
  const [showAddVehicleDialog, setShowAddVehicleDialog] = useState(false);
  const [selectedPesticideTypes, setSelectedPesticideTypes] = useState([]);
  const [isChecked, setIsChecked] = useState(false);
  const [distanceChecked, setDistanceChecked] = useState(false);
  const [vegetationChecked, setVegetationChecked] = useState(false);
  const [sprayingTechniqueChecked, setSprayingTechniqueChecked] = useState(false);
  const [nozzleProvidedChangeChecked, setNozzleProvidedChangeChecked] = useState(false);
  const [closeEndedSprayChecked, setCloseEndedSprayChecked] = useState(false);
  const [optimalDistributionChecked, setOptimalDistributionChecked] = useState(false);
  const [sprayingTechniqueStoppedChecked, setSprayingTechniqueStoppedChecked] = useState(false);
  const selectedColor = '#00696D';

  useImperativeHandle(ref, () => ({
    isValid: () => {

      const waterJournalCoreAttributesValid = (
        waterJournal?.equipmentModel &&
        waterJournal?.nozzleName &&
        waterJournal?.pressure &&
        waterJournal?.driftReduction &&
        waterJournal?.maxDrivingSpeed &&
        waterJournal?.airSpeed &&
        waterJournal?.ptoSpeed &&
        waterJournal?.amount &&
        waterJournal?.sprayingTechniqueMeter &&
        waterJournal?.distanceToWaterBody &&
        waterJournal?.distanceToWaterWell &&
        (isChecked ? waterJournal?.bufferZonePlacement && waterJournal?.locationAndDistance : true) &&
        (distanceChecked ? waterJournal?.distanceOfSurfaceWater : true) &&
        (vegetationChecked ? waterJournal?.distanceOfVegitagedBufferZone : true) &&
        (waterJournal?.windSpeed3)
      );

      const isWaterJournalValid = waterJournalCoreAttributesValid;

      let isPesticideValid = false;

      if (formData.pesticideType?.length > 0) {
        setSelectedPesticideTypes(formData.pesticideType);

        isPesticideValid = formData.pesticideType.every((pesticideName, index) => {
          const amountKey = `pesticideType.${index}.amount`;
          const unitKey = `pesticideType.${index}.unit`;
          return formData[amountKey] && formData[unitKey];
        });
        // eslint-disable-next-line
        formData.pesticideType.every((pesticideName, index) => {
          const amountKey = `pesticideType.${index}.amount`;
          const unitKey = `pesticideType.${index}.unit`;
          formData[amountKey] = formData[`pesticideType.${index}.amount`];
          formData[unitKey] = formData[`pesticideType.${index}.unit`];
        });
      }

      const isPestSubtypeValid = formData?.taskActivity?.pestData;
      // console.log('isPestSubtypeValid: ', isPestSubtypeValid);

      const isGrowthStageValid = formData?.taskActivity?.growthStage;

      const requiredFields = [
        formData.taskActivity?.crop,
        isWaterJournalValid,
        isPesticideValid,
        isPestSubtypeValid,
        isGrowthStageValid,
        formData?.reason,
        formData?.effectApplication,
      ];

      const hasFormErrors = formErrors && Object.values(formErrors).some(error => error);
      // console.log('requiredFields: ', requiredFields);
      // const isValid = requiredFields.every(Boolean);
      // console.log('isValid', isValid);

      return requiredFields.every(Boolean) && !hasFormErrors;
    },
  }));

  const handleChange = (event) => {
    setIsChecked(event.target.checked);
  };

  const handleDistanceChange = (event) => {
    setDistanceChecked(event.target.checked);
  };

  const handleVegetationChange = (event) => {
    setVegetationChecked(event.target.checked);
  };

  const handleSprayingTechnique = (event) => {
    setSprayingTechniqueChecked(event.target.checked);
  }

  const handleNozzleProvidedChange = (event) => {
    setNozzleProvidedChangeChecked(event.target.checked);
  }

  const handleCloseEndedSprayChange = (event) => {
    setCloseEndedSprayChecked(event.target.checked);
  }

  const handleOptimalDistributionChange = (event) => {
    setOptimalDistributionChecked(event.target.checked);
  }

  const handleSprayingTechniqueStopped = (event) => {
    setSprayingTechniqueStoppedChecked(event.target.checked);
  }

  const handlePesticideTypeChange = (event) => {
    setSelectedPesticideTypes(event.target.value);
    if (onFieldChange) {
      onFieldChange('pesticideType', event.target.value);
    }
  };

  // Effect of pesticide application
  const getEffectOfApplicationValues = () => {
    const values = ['God', 'Ok', 'Dårlig'];
    return values.map(value => ({
      value,
      label: value,
    }));
  };

  const getWindSpeedMoreThanThreeValues = () => {
    return [
      { value: 'true', label: 'Ja' },
      { value: 'false', label: 'Nei' }
    ];
  }

  // Crop List
  const getDropDownFieldsStatuses = dropdownList => {
    return dropdownList?.map(item => ({
      value: item.name,
      label: item.name
    }));
  };

  // Pesticide type options
  const pesticideTypeOptions = (activitiesCommon) => {
    const pesticideActivity = activitiesCommon?.find(
      (activity) => activity.activityId === 'act19'
    );
    const pesticideDropdown = pesticideActivity?.dropdowns?.find(
      (dropdown) => dropdown.element === 'pesticide'
    );
    return pesticideDropdown?.leaves || [];
  };

  // Wind speed
  const getWindSpeedOptions = () => {
    return Array.from({ length: 20 }, (_, i) => {
      const value = (i + 1).toString();
      return {
        value,
        label: value,
      };
    });
  };

  let pestDataValue = {
    element: formData?.taskActivity?.pestData,
    leaves: formData?.taskActivity['pestSubList'] || null
  }

  let growthStageValue = {
    element: formData.taskActivity?.growthStage || null,
    subElement: formData.taskActivity?.growthStageSub || null,
    leaf: formData.taskActivity?.growthStageSubSubType || null
  }
  // console.log("growthStageValue: ", growthStageValue);

  return (
    <Box sx={{
      overflowY: 'auto',
      '&::-webkit-scrollbar': {
        width: '10px',
        backgroundColor: 'rgba(0, 0, 0, 0.1)',
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: '#A8A8A8',
        borderRadius: '4px',
        transition: 'background-color 0.3s ease',
      },
      '&::-webkit-scrollbar-thumb:hover': {
        backgroundColor: '#B8B8B8',
      }
    }}
    >
      <Box sx={{ display: "flex", paddingTop: '56px', alignItems: "center" }}>
        {/* Equipment model */}
        <MuiTextField
          defaultLabel={intl.formatMessage({ id: "EQUIPMENT_MODEL" })}
          defaultValue={formData.waterJournal.eqiupmentModel ?? ''}
          fieldName='equipmentModel'
          onChange={onFieldChange}
          required={true}
          activityId="act19"
        />
      </Box>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {/* Nozzle name */}
        <MuiTextField
          defaultLabel={intl.formatMessage({ id: "NOZZLE_NAME_WATER_JOURNAL" })}
          defaultValue={formData.waterJournal.nozzleName ?? ''}
          fieldName='nozzleName'
          onChange={onFieldChange}
          required={true}
          activityId="act19"
        />
      </Box>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {/* Pressure */}
        <MuiTextField
          defaultLabel={intl.formatMessage({ id: 'PRESSURE_WATER_JOURNAL' })}
          defaultValue={formData.waterJournal.pressure ?? ''}
          fieldName='pressure'
          numberKeypad={true}
          onChange={handleNumericValidation}
          inputAdornment={intl.formatMessage({ id: 'WATER_JOURNAL_UNIT_BAR' })}
          autoFocus={false}
          errorHelperText={formErrors['pressure']}
          required={true}
          activityId="act19"
        />
      </Box>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {/* Drift reduction */}
        <MuiTextField
          defaultLabel={intl.formatMessage({ id: 'WATER_JOURNAL_DRIFT_REDUCTION' })}
          defaultValue={formData.waterJournal.driftReduction ?? ''}
          fieldName='driftReduction'
          numberKeypad={true}
          onChange={handleNumericValidation}
          autoFocus={false}
          inputAdornment={intl.formatMessage({ id: 'WATER_JOURNAL_UNIT_PERCENTAGE' })}
          errorHelperText={formErrors['driftReduction']}
          required={true}
          activityId="act19"
        />
      </Box>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {/* Driving speed */}
        <MuiTextField
          defaultLabel={intl.formatMessage({ id: 'WATER_JOURNAL_DRIVING_SPEED' })}
          defaultValue={formData.waterJournal.maxDrivingSpeed ?? ''}
          fieldName='maxDrivingSpeed'
          numberKeypad={true}
          onChange={handleNumericValidation}
          inputAdornment={intl.formatMessage({ id: 'WATER_JOURNAL_UNIT_KM' })}
          autoFocus={false}
          errorHelperText={formErrors['maxDrivingSpeed']}
          required={true}
          activityId="act19"
        />
      </Box>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {/* New field - 36 airSpeed */}
        <MuiTextField
          defaultLabel={intl.formatMessage({ id: 'AIR_SPEED' })}
          defaultValue={formData.waterJournal.airSpeed ?? ''}
          fieldName="airSpeed"
          numberKeypad={true}
          onChange={handleNumericValidation}
          autoFocus={false}
          inputAdornment={intl.formatMessage({ id: 'WATER_JOURNAL_UNIT_M/SEK' })}
          error={!!formErrors['airSpeed']}
          helperText={formErrors['airSpeed']}
          required={true}
          activityId="act19"
        />
      </Box>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {/* Pto speed */}
        <MuiTextField
          defaultLabel={intl.formatMessage({ id: 'WATER_JOURNAL_PTO_SPEED' })}
          defaultValue={formData.waterJournal.ptoSpeed ?? ''}
          fieldName="ptoSpeed"
          numberKeypad={true}
          onChange={handleNumericValidation}
          autoFocus={false}
          inputAdornment={intl.formatMessage({ id: 'WATER_JOURNAL_UNIT_OMDR' })}
          error={!!formErrors['ptoSpeed']}
          helperText={formErrors['ptoSpeed']}
          required={true}
          activityId="act19"
        />
      </Box>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {/* water journal amount */}
        <MuiTextField
          defaultLabel={intl.formatMessage({ id: "WATER_JOURNAL_AMOUNT" })}
          defaultValue={formData.waterJournal.amount ?? ''}
          fieldName='amount'
          numberKeypad={true}
          onChange={handleNumericValidation}
          inputAdornment={intl.formatMessage({ id: 'WATER_JOURNAL_UNIT_L' })}
          autoFocus={false}
          errorHelperText={formErrors['amount']}
          required={true}
          activityId="act19"
        />
      </Box>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {/* New field - 39  sprayingTechniqueMeter*/}
        <MuiTextField
          defaultLabel={intl.formatMessage({ id: 'SPRAYING_TECHNIQUE_DISTANCE_PER_METER' })}
          defaultValue={formData.waterJournal.sprayingTechniqueMeter ?? ''}
          fieldName='sprayingTechniqueMeter'
          numberKeypad={true}
          inputAdornment={intl.formatMessage({ id: 'WATER_JOURNAL_UNIT_M' })}
          onChange={handleNumericValidation}
          autoFocus={false}
          errorHelperText={formErrors['sprayingTechniqueMeter']}
          required={true}
          activityId="act19"
        />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
        <Box>
          {/* Is the spraying technique used on the entire, or a certain number of meters of the shift? */}
          <FormControlLabel
            control={
              <Checkbox
                checked={sprayingTechniqueChecked}
                onChange={(e) => {
                  const checked = e.target.checked
                  handleSprayingTechnique(e)
                  onFieldChange('sprayingTechnique', checked)
                }}
                sx={{
                  paddingRight: '8px', // Add some spacing between checkbox and label
                  color: sprayingTechniqueChecked ? selectedColor : null,
                  '&.Mui-checked': {
                    color: selectedColor,
                  },
                }}
              />
            }
            label={intl.formatMessage({ id: 'SPRAYING_TECHNIQUE_USED_ON_ALL' })}
            sx={{
              ...taskTemplateStyle.modalDialogSubTitle,
              whiteSpace: 'nowrap', // Prevent text from wrapping
              marginRight: '0px'
            }}
          />
        </Box>
        <Box sx={{ paddingTop: '24px' }}>
          {/* Have nozzles been used that provide targeted movement of the spray liquid? */}
          <FormControlLabel
            control={
              <Checkbox
                checked={nozzleProvidedChangeChecked}
                onChange={(e) => {
                  const checked = e.target.checked
                  handleNozzleProvidedChange(e)
                  onFieldChange('nozzlesProvideTargetMovement', checked)
                }}
                sx={{
                  paddingRight: '8px',
                  color: nozzleProvidedChangeChecked ? selectedColor : null,
                  '&.Mui-checked': {
                    color: selectedColor,
                  },
                }}
              />
            }
            label={intl.formatMessage({ id: 'NOZZLES_PROVIDED_SPRAYING_TECHNIQUE' })}
            sx={{
              ...taskTemplateStyle.modalDialogSubTitle,
              overflow: 'hidden',
              whiteSpace: 'normal',
              textOverflow: 'ellipsis',
              maxWidth: '100%',
              textAlign: 'left'
            }}
          />
        </Box>
        <Box sx={{ paddingTop: '24px' }}>
          {/* Is one side closed to avoid spraying against surface water? */}
          <FormControlLabel
            control={
              <Checkbox
                checked={closeEndedSprayChecked}
                onChange={(e) => {
                  const checked = e.target.checked
                  handleCloseEndedSprayChange(e)
                  onFieldChange('oneEndClosedToAvoidSurfaceWater', checked)
                }}
                sx={{
                  paddingRight: '8px',
                  color: closeEndedSprayChecked ? selectedColor : null,
                  '&.Mui-checked': {
                    color: selectedColor,
                  },
                }}
              />
            }
            label={intl.formatMessage({ id: 'CLOSE_ENDED_SPRAY' })}
            sx={{
              ...taskTemplateStyle.modalDialogSubTitle,
              overflow: 'hidden',
              whiteSpace: 'normal',
              textOverflow: 'ellipsis',
              maxWidth: '100%',
              textAlign: 'left'
            }}
          />
        </Box>
        <Box sx={{ paddingTop: '24px' }}>
          {/* Are the dampers set for optimal distribution of liquid and air? */}
          <FormControlLabel
            control={
              <Checkbox
                checked={optimalDistributionChecked}
                onChange={(e) => {
                  const checked = e.target.checked;
                  handleOptimalDistributionChange(e)
                  onFieldChange('damperSetForOptimalDistribution', checked)
                }}
                sx={{
                  paddingRight: '8px',
                  color: optimalDistributionChecked ? selectedColor : null,
                  '&.Mui-checked': {
                    color: selectedColor,
                  },
                }}
              />
            }
            label={intl.formatMessage({ id: 'OPTIMAL_DISTRIBUTION_OF_LIQUID' })}
            sx={{
              ...taskTemplateStyle.modalDialogSubTitle,
              overflow: 'hidden',
              whiteSpace: 'normal',
              textOverflow: 'ellipsis',
              maxWidth: '100%',
              textAlign: 'left'
            }}
          />
        </Box>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", paddingTop: '56px' }}>
        <Typography style={taskTemplateStyle.textHeadingStyle}>
          {intl.formatMessage({ id: 'FIELD_DETAILS' })}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", paddingTop: '24px', alignItems: "center" }}>
        {/* Crop */}
        <MuiSelectMobileDropdown
          defaultLabel={`${intl.formatMessage({ id: 'CT_CR' })}*`}
          defaultValue={formData.taskActivity?.crop || ''}
          fieldName='crop'
          options={getDropDownFieldsStatuses(fieldMasterData.cropList)}
          autoFocus={false}
          required={true}
          onChange={onFieldChange}
          bottomSheetHeight="41vh"
          activityId="act19"
        />
      </Box>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {/* field age */}
        <MuiTextField
          defaultLabel={intl.formatMessage({ id: 'WATER_JOURNAL_FIELD_AGE' })}
          defaultValue={formData.waterJournal.fieldAge ?? ''}
          fieldName='fieldAge'
          numberKeypad={true}
          onChange={handleNumericValidation}
          inputAdornment={intl.formatMessage({ id: 'WATER_JOURNAL_UNIT_AR' })}
          required={true}
          errorHelperText={formErrors['fieldAge']}
          activityId="act19"
        />
      </Box>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {/* culture by spray */}
        <MuiTextField
          defaultLabel={intl.formatMessage({ id: "WATER_JOURNAL_CULTURE_SPRAY" })}
          defaultValue={formData.waterJournal.cultureBySpray ?? ''}
          fieldName='cultureBySpray'
          numberKeypad={true}
          onChange={handleNumericValidation}
          autoFocus={false}
          inputAdornment={intl.formatMessage({ id: 'WATER_JOURNAL_UNIT_M' })}
          errorHelperText={formErrors['cultureBySpray']}
          required={true}
          activityId="act19"
        />
      </Box>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {/* development stage */}
        <BottomSheetSubNestedDropdown
          label={`${intl.formatMessage({ id: 'DEVELOPMENT_STAGE_ST' })}*`}
          bottomSheetTitleText={intl.formatMessage({ id: 'GROWTH_STAGE_LABEL' })}
          options={growthStageDropdowns}
          defaultValue={growthStageValue}
          onChange={(value) => onFieldChange('growthStageNested', value)}
          required={true}
          // activityId="act19"
        />
      </Box>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {/* Nozzle factory */}
        <MuiTextField
          defaultLabel={intl.formatMessage({ id: 'WATER_JOURNAL_DISTANCE_TO_WATER_BODY' })}
          defaultValue={formData.waterJournal.distanceToWaterBody ?? ''}
          fieldName='distanceToWaterBody'
          numberKeypad={true}
          onChange={handleNumericValidation}
          inputAdornment={intl.formatMessage({ id: 'WATER_JOURNAL_UNIT_M' })}
          required={true}
          errorHelperText={formErrors['distanceToWaterBody']}
          activityId="act19"
        />
      </Box>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {/* Nozzle height */}
        <MuiTextField
          defaultLabel={intl.formatMessage({ id: "WATER_JOURNAL_DISTANCE_TO_WATER_WELL" })}
          defaultValue={formData.waterJournal.distanceToWaterWell ?? ''}
          fieldName='distanceToWaterWell'
          numberKeypad={true}
          onChange={handleNumericValidation}
          autoFocus={false}
          errorHelperText={formErrors['distanceToWaterWell']}
          inputAdornment={intl.formatMessage({ id: 'WATER_JOURNAL_UNIT_M' })}
          required={true}
          activityId="act19"
        />
      </Box>
      <Box sx={{ display: "flex", paddingTop: '32px', alignItems: "center" }}>
        {/* vegetated buffer exists */}
        <FormControlLabel
          control={<Checkbox
            checked={isChecked}
            onChange={(e) => {
              const checked = e.target.checked
              handleChange(e)
              onFieldChange('vegetatedBufferExists', checked)
            }}
            sx={{
              color: isChecked ? selectedColor : null,
              '&.Mui-checked': {
                color: selectedColor,
              },
            }}
          />}
          label={`${intl.formatMessage({ id: 'ESTABLISHED_BUFFER_ZONE' })}?`}
          sx={taskTemplateStyle.modalDialogSubTitle}
        />
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", paddingTop: '24px' }}>
        {/* New field - 26 bufferzonePlacement*/}
        <MuiTextField
          defaultLabel={intl.formatMessage({ id: "LOCATION_ON_BUFFER_ZONE" })}
          defaultValue={formData.waterJournal.bufferZonePlacement ?? ''}
          fieldName='bufferZonePlacement'
          numberKeypad={true}
          onChange={onFieldChange}
          autoFocus={false}
          required={isChecked ? true : false}
          errorHelperText={formErrors['bufferZonePlacement']}
          disabled={!isChecked} // Disable based on isChecked
          activityId="act19"
        />
      </Box>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {/* New field - 27 locationAndDistance */}
        <MuiTextField
          defaultLabel={intl.formatMessage({ id: 'LENGTH_BUFFER_ZONE' })}
          defaultValue={formData.waterJournal.locationAndDistance ?? ''}
          fieldName='locationAndDistance'
          numberKeypad={true}
          onChange={handleNumericValidation}
          autoFocus={false}
          required={isChecked ? true : false}
          inputAdornment={intl.formatMessage({ id: 'WATER_JOURNAL_UNIT_M' })}
          errorHelperText={formErrors['locationAndDistance']}
          disabled={!isChecked} // Disable based on isChecked
          activityId="act19"
        />
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", paddingTop: '32px' }}>
        <Typography style={taskTemplateStyle.textHeadingStyle}>
          {intl.formatMessage({ id: 'PESTICIDE_INFO' })}
        </Typography>
      </Box>
      <Box sx={{ width: '100%', display: "flex", flexDirection: 'column', alignItems: "flex-start", paddingTop: '24px' }}>
        <MuiMultiSelectBottomSheet
          label={`${intl.formatMessage({ id: 'TYPE_OF_PESTICIDE' })}*`}
          value={selectedPesticideTypes}
          name='pesticideType.name'
          options={pesticideTypeOptions(activitiesCommon)}
          onChange={handlePesticideTypeChange}
          autoFocus={false}
          fullWidth={true}
          required={true}
          errorHelperText={formErrors['pesticideType.name']}
          bottomSheetHeight="60vh"
          customSx={{
            width: '100%',
            marginLeft: 0,
            '& .MuiFormControl-root': {
              width: '100% !important',
              marginLeft: 0,
            },
            '& .MuiOutlinedInput-root': {
              width: '100% !important',
            },
            '& .MuiSelect-select': {
              width: '100% !important',
            },
          }}
        />

        {selectedPesticideTypes.map((pesticideName, index) => {
          const fieldKey = `pesticideType[${pesticideName || 'default'}]`;
          return (
            <Box
              key={fieldKey}
              sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}
            >
              <Typography sx={{ paddingBottom: '10px' }}>{pesticideName}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                {/* Pesticide Amount */}
                <MuiTextField
                  defaultLabel={intl.formatMessage({ id: 'AMOUNT_OF_PESTICIDE' })}
                  defaultValue={formData[`pesticideType.${index}.amount`] || ''}
                  fieldName={`pesticideType.${index}.amount`}
                  numberKeypad={true}
                  onChange={handleNumericValidation}
                  autoFocus={false}
                  required={true}
                  errorHelperText={formErrors[`pesticideType.${index}.amount`]}
                  fullWidth
                  activityId="act19"
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                {/* Pesticide Unit */}
                <MuiSelectMobileDropdown
                  defaultLabel={`${intl.formatMessage({ id: 'CT_UNIT' })}*`}
                  defaultValue={formData[`pesticideType.${index}.unit`] || ''}
                  fieldName={`pesticideType.${index}.unit`}
                  options={pesticideUnitOptions()}
                  required={true}
                  autoFocus={false}
                  fullWidth
                  onChange={onFieldChange}
                  activityId="act19"
                />
              </Box>
            </Box>
          );
        })}
        {selectedPesticideTypes.length === 0 && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              {/* Pesticide Amount - Shown when no pesticide types are selected */}
              <MuiTextField
                defaultLabel={intl.formatMessage({ id: 'AMOUNT_OF_PESTICIDE' })}
                defaultValue={formData.pesticideType?.default?.amount || ''}
                fieldName="pesticideType.0.amount"
                numberKeypad={true}
                onChange={handleNumericValidation}
                autoFocus={false}
                required={true}
                errorHelperText={formErrors['pesticideType.0.amount']}
                fullWidth
                activityId="act19"
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              {/* Pesticide Unit - Shown when no pesticide types are selected */}
              <MuiSelectMobileDropdown
                defaultLabel={`${intl.formatMessage({ id: 'CT_UNIT' })}*`}
                defaultValue={formData.pesticideType?.default?.unit || ''}
                fieldName="pesticideType.0.unit"
                options={pesticideUnitOptions()}
                required={true}
                autoFocus={false}
                fullWidth
                onChange={onFieldChange}
                activityId="act19"
              />
            </Box>
          </>
        )}
      </Box>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {/* Pest including subtypes  */}
        <BottomSheetNestedDropdown
          label={`${intl.formatMessage({ id: 'PEST_SUBTYPE' })}*`}
          bottomSheetTitleText={intl.formatMessage({ id: 'PEST_DATA_LABEL' })}
          options={pesticideDropdowns}
          defaultValue={pestDataValue}
          onChange={(value) => onFieldChange('pestInfo', value)}
          required={true}
          activityId="act19"
        />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', paddingTop: '32px' }}>
        {/* New field - 28 distanceOfSurfaceWaterExists */}
        <FormControlLabel
          control={
            <Checkbox
              checked={distanceChecked}
              onChange={(e) => {
                const checked = e.target.checked;
                handleDistanceChange(e);
                onFieldChange('distanceOfSurfaceWaterExists', checked);
              }}
              sx={{
                paddingRight: '5px',
                color: distanceChecked ? selectedColor : null,
                '&.Mui-checked': {
                  color: selectedColor,
                },
              }}
            />
          }
          label={intl.formatMessage({ id: 'DISTANCE_REQUIREMENTS_SURFACE_WATER' })}
          sx={{
            ...taskTemplateStyle.modalDialogSubTitle,
            '& .MuiFormControlLabel-label': {
              textAlign: 'left',
              '& > span': { // Target the element wrapping the text
                display: 'inline-block', // Allows margin manipulation
                marginLeft: '-0.5em', // Adjust this negative value to shift left
              },
            },
            marginRight: '0px'
          }}
        />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', paddingTop: '24px' }}>
        {/* New field - 30 distanceOfSurfaceWater */}
        <MuiTextField
          defaultLabel={intl.formatMessage({ id: 'DISTANCE_REQUIREMENTS' })}
          defaultValue={formData.waterJournal.distanceOfSurfaceWater ?? ''}
          fieldName="distanceOfSurfaceWater"
          onChange={handleNumericValidation}
          autoFocus={false}
          numberKeypad={true}
          inputAdornment={intl.formatMessage({ id: 'WATER_JOURNAL_UNIT_M' })}
          error={!!formErrors['distanceOfSurfaceWater']}
          helperText={formErrors['distanceOfSurfaceWater']}
          sx={taskTemplateStyle.checkboxSelectedAttributesStyle}
          required={distanceChecked ? true : false}
          disabled={!distanceChecked} // Disable based on distanceChecked
          activityId="act19"
        />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
        {/* vegitatedBufferZone */}
        <FormControlLabel
          control={
            <Checkbox
              checked={vegetationChecked}
              onChange={(e) => {
                const checked = e.target.checked;
                handleVegetationChange(e)
                onFieldChange('vegitatedBufferZone', checked);
              }}
              sx={{
                paddingRight: '5px',
                color: vegetationChecked ? selectedColor : null,
                '&.Mui-checked': {
                  color: selectedColor,
                },
              }}
            />
          }
          label={intl.formatMessage({ id: 'VEGETATED_BUFFER_ZONE_REQUIREMENTS' })}
          sx={{
            ...taskTemplateStyle.modalDialogSubTitle,
            '& .MuiFormControlLabel-label': {
              textAlign: 'left',
              '& > span': { // Target the element wrapping the text
                display: 'inline-block', // Allows margin manipulation
                marginLeft: '-0.5em', // Adjust this negative value to shift left
              },
            },
            marginRight: '0px'
          }}
        />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', paddingTop: '24px' }}>
        {/* New field - 31 distanceOfVegitagedBufferZone */}
        <MuiTextField
          defaultLabel={intl.formatMessage({ id: 'BUFFER_ZONE_REQUIREMENTS' })}
          defaultValue={formData.waterJournal.distanceOfVegitagedBufferZone ?? ''}
          fieldName="distanceOfVegitagedBufferZone"
          onChange={handleNumericValidation}
          autoFocus={false}
          numberKeypad={true}
          inputAdornment={intl.formatMessage({ id: 'WATER_JOURNAL_UNIT_M' })}
          error={!!formErrors['distanceOfVegitagedBufferZone']}
          helperText={formErrors['distanceOfVegitagedBufferZone']}
          sx={taskTemplateStyle.checkboxSelectedAttributesStyle}
          required={vegetationChecked ? true : false}
          disabled={!vegetationChecked} // Disable based on vegetationChecked
          activityId="act19"
        />
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", paddingTop: '32px' }}>
        <Typography style={taskTemplateStyle.textHeadingStyle}>
          {intl.formatMessage({ id: 'AF_OTHER' })}
        </Typography>
      </Box>
      {/* Reason for pesticide application */}
      <Box sx={{ display: "flex", alignItems: "center", paddingTop: '24px' }}>
        <MuiTextField
          defaultLabel={intl.formatMessage({ id: 'RFPA' })}
          defaultValue={formData.reason}
          fieldName="reason"
          onChange={onFieldChange}
          required={true}
          error={!!formErrors['reason']}
          helperText={formErrors['reason']}
          activityId="act19"
        />
      </Box>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {/* Effect of application */}
        <MuiSelectMobileDropdown
          defaultLabel={`${intl.formatMessage({ id: 'EFFECT' })}*`}
          defaultValue={formData.effectApplication}
          fieldName='effectApplication'
          options={getEffectOfApplicationValues()}
          autoFocus={false}
          required={true}
          onChange={onFieldChange}
          activityId="act19"
        />
      </Box>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {/* New field - 46 isWindspeed3 */}
        <MuiSelectMobileDropdown
          defaultLabel={`${intl.formatMessage({ id: 'WIND_SPEED_MORE_THAN_3' })}*`}
          defaultValue={formData.waterJournal.windSpeed3 ?? ''}
          fieldName='windSpeed3'
          options={getWindSpeedMoreThanThreeValues()}
          autoFocus={false}
          required={true}
          onChange={onFieldChange}
          activityId="act19"
        />
      </Box>
      {/* New field - 47 sprayingIntoCrop */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={sprayingTechniqueStoppedChecked}
              onChange={(e) => {
                const checked = e.target.checked
                handleSprayingTechniqueStopped(e)
                onFieldChange('sprayingIntoCrop', checked)
              }}
              sx={{
                paddingRight: '8px',
                color: sprayingTechniqueStoppedChecked ? selectedColor : null,
                '&.Mui-checked': {
                  color: selectedColor,
                },
              }}
            />
          }
          label={intl.formatMessage({ id: 'SPRAYING_STOPPED_INSIDE_CULTURE' })}
          sx={{
            ...taskTemplateStyle.modalDialogSubTitle,
            overflow: 'hidden',
            whiteSpace: 'normal',
            textOverflow: 'ellipsis',
            maxWidth: '100%',
            textAlign: 'left'
          }}
        />
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", paddingTop: '56px' }}>
        <MuiTextFieldArea
          defaultLabel={intl.formatMessage({ id: 'NOTE' })}
          fieldName='note'
          onChange={onFieldChange}
        />
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", paddingTop: '56px' }}>
        <MuiCollapseComponent
          title={intl.formatMessage({ id: 'TASK_MORE_INFORMATION_OPTIONAL' })}
          content={
            <Box sx={{ width: '100%', padding: '0px' }}>
              <Box sx={{ display: "flex", alignItems: "center", paddingTop: '48px' }}>
                <MuiTextField
                  defaultLabel={intl.formatMessage({ id: 'TIME_SPENT' })}
                  defaultValue={`${currentTimerHourValue} : ${currentTimerMinutesValue}`}
                  inputAdornment={<AccessTimeIcon />}
                  position="start"
                  fieldName='timeSpent'
                  autoFocus={false}
                  onClick={openTimerDialog}
                  readOnly={true}
                  activityId="act19"
                />
              </Box>
              {/* Wind */}
              <Box sx={{ display: "flex", alignItems: "center", paddingTop: '32px' }}>
                <MuiSelectMobileDropdown
                  defaultLabel={intl.formatMessage({ id: 'WIND' })}
                  defaultValue={formData.windSpeed}
                  inputAdornment={intl.formatMessage({ id: 'WIND_UNIT' })}
                  fieldName="windSpeed"
                  options={getWindSpeedOptions()}
                  autoFocus={false}
                  onChange={onFieldChange}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: '250px', // Adjust this value (in pixels) to your desired height
                      },
                    },
                  }}
                  activityId="act19"
                />
              </Box>
              {/* Vehicle */}
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <MuiSelectMobileDropdown
                  key={createdVehicleId}
                  defaultLabel={intl.formatMessage({ id: 'VEHICLE' })}
                  defaultValue={createdVehicleId}
                  options={vehicleOptions()}
                  fieldName="vehicleAssetId"
                  onChange={onFieldChange}
                  activityId="act19"
                />
              </Box>
              {/* Performed By */}
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <MuiTextField
                  defaultLabel={intl.formatMessage({ id: 'PERFORMED_BY' })}
                  defaultValue={formData.performedBy}
                  fieldName="performedBy"
                  onChange={onFieldChange}
                  activityId="act19"
                />
              </Box>
            </Box>
          }
        />
      </Box>
      <AddVehicleModal
        intl={intl}
        vehicleSelectedId={'new'}
        onCloseVehicleDialog={onCloseVehicleDialog}
        showAddVehicleDialog={showAddVehicleDialog}
        assetType={'vehicle'}
        taskDetails={taskDetails}
      />
      <Box>
        <TimerModal
          intl={intl}
          isTimerOpen={isTimerOpen}
          handleCancelTimerButton={handleCancelTimerButton}
          handleOkTimerButton={handleOkTimerButton}
          handleHoursChange={handleHoursChange}
          handleMinutesChange={handleMinutesChange}
          formErrors={formErrors}
          formState={formData}
        />
      </Box>
    </Box>
  );
});

export default AddTaskWaterJournalTemplate2;
